import {
  BusinessGetResponseType,
  OrderGetResponseType,
} from "@sellar-io/api-v2/types";
import { useNavigate } from "react-router";
import {
  BuyerOnly,
  SupplierOnly,
  DropdownLegacyElement,
} from "../../../../components";
import { AdminOnly } from "../../../../components/authFilter/AdminOnly";
import {
  ORDER_DELIVERY_NOTE,
  PAYMENT_LINK_REF,
} from "../../../../constants/routes";
import { ORDER_FULFILLMENT_TYPES } from "../../../../constants/sellarConstants.shared";
import { useFeatureFlagStore } from "../../../../hooks/useFeatureFlags";
import { usePaymentList } from "../../../../hooks/api/v2/payment/usePaymentList";
import { useBuildStripeDashboardUrl } from "../../../../utils/stripeDashboardUrl";
import { OrderContentActions, OrderContentState } from "./useOrderContentState";
import {
  showIssueCreditNote,
  showIssueRefund,
  showIssueSellarImportCreditNote,
  showOrderCancel,
  showOrderDelivered,
  showOrderInvoice,
} from "./orderContentHelpers";

type OrderActionsDropdownProps = {
  order: OrderGetResponseType;
  business: BusinessGetResponseType;
  canUseSellarCreditFeatures: boolean;
  supplierUsesPalletInboundForOrder: boolean;
  orderHasPalletOrderShipment: boolean;
  showMarkOrderWithIncompleteStripePaymentAsPaid: boolean;
  showSendCardPaymentLink: boolean;
  showCardPaymentLink: boolean;
  userEmail: string | undefined;
  state: OrderContentState;
  actions: OrderContentActions;
  onCancelFlow: () => void;
  onSendCardPaymentLink: () => void;
};

export const OrderActionsDropdown = ({
  order,
  business,
  canUseSellarCreditFeatures,
  supplierUsesPalletInboundForOrder,
  orderHasPalletOrderShipment,
  showMarkOrderWithIncompleteStripePaymentAsPaid,
  showSendCardPaymentLink,
  showCardPaymentLink,
  userEmail,
  state,
  actions,
  onCancelFlow,
  onSendCardPaymentLink,
}: OrderActionsDropdownProps) => {
  const navigate = useNavigate();
  const buildStripeDashboardUrl = useBuildStripeDashboardUrl();
  const { isActive } = useFeatureFlagStore();

  const cardDepositPaymentsQuery = usePaymentList(
    {
      query: [
        {
          type: "OrderId",
          modifier: "EQUALS",
          value: order.id,
        },
      ],
    },
    {
      enabled: order.paymentType === "cardPaymentWithDeposit",
    },
  );

  const hasCardDepositBeenPaid =
    !!cardDepositPaymentsQuery.data?.pages[0]?.some(
      (payment) => payment.type === "cardDeposit" && payment.status === "paid",
    );

  const showRecordCardPaymentWithDepositManualPayment =
    order.paymentType === "cardPaymentWithDeposit" &&
    order.paymentStatus !== "paid" &&
    hasCardDepositBeenPaid &&
    !!cardDepositPaymentsQuery.data?.pages[0]?.some(
      (payment) =>
        payment.type === null &&
        (payment.status === "failed" || payment.status === "pending"),
    );

  const showSupplierOrBuyerActions =
    (business.Retailer && order.confirmedAt && !order.cancelledAt) ||
    (business.Supplier && !order.cancelledAt);

  return (
    <>
      {showSupplierOrBuyerActions && (
        <>
          <SupplierOnly>
            {showOrderDelivered(order) && (
              <DropdownLegacyElement
                onClick={() => actions.setOrderDeliveredDialogActive(true)}
              >
                Delivered
              </DropdownLegacyElement>
            )}
            {!order.paidAt &&
              order.paymentType !== "sellarCredit" &&
              order.paymentType !== "cardPaymentWithDeposit" &&
              order.paymentType !== "cardPayment" && (
                <DropdownLegacyElement
                  onClick={() => actions.setOrderPaidDialogActive(true)}
                >
                  Mark as paid
                </DropdownLegacyElement>
              )}
            {order.paymentType === "stripe" &&
              order.stripeInvoiceId &&
              order.invoiceFinalizedAt &&
              order.Supplier.stripeAccountId && (
                <DropdownLegacyElement
                  onClick={() =>
                    window.open(
                      buildStripeDashboardUrl({
                        type: "invoice",
                        stripeAccountId: order.Supplier.stripeAccountId!,
                        id: order.stripeInvoiceId!,
                      }),
                      "_blank",
                    )
                  }
                >
                  View on Stripe
                </DropdownLegacyElement>
              )}
            {order.confirmedAt &&
              !order.deliveredAt &&
              order.fulfillmentType ===
                ORDER_FULFILLMENT_TYPES.DIRECT.value && (
                <DropdownLegacyElement
                  onClick={() =>
                    window.open(
                      `https://www.google.com/maps/dir/?api=1&destination=${`${order.DeliveryAddress.name}, ${order.DeliveryAddress.street}, ${order.DeliveryAddress.city}, ${order.DeliveryAddress.postalCode}`.replace(
                        " ",
                        "+",
                      )}`,
                      "_blank",
                    )
                  }
                >
                  Directions
                </DropdownLegacyElement>
              )}
            {order.confirmedAt && (
              <>
                <DropdownLegacyElement
                  onClick={() =>
                    navigate(
                      `${ORDER_DELIVERY_NOTE.href({ orderId: order.id })}${location.search}`,
                    )
                  }
                >
                  Save delivery notes
                </DropdownLegacyElement>
                <DropdownLegacyElement
                  onClick={() =>
                    navigate(
                      `${ORDER_DELIVERY_NOTE.href({ orderId: order.id })}${location.search}&pricing=true`,
                    )
                  }
                >
                  Save delivery notes (inc pricing)
                </DropdownLegacyElement>
              </>
            )}
            {showIssueCreditNote({ order, canUseSellarCreditFeatures }) && (
              <DropdownLegacyElement
                onClick={() => actions.setCreateCreditNoteDialogActive(true)}
              >
                Create credit note
              </DropdownLegacyElement>
            )}
            {showIssueSellarImportCreditNote({ order }) && (
              <DropdownLegacyElement
                onClick={() =>
                  actions.setCreateSellarImportCreditNoteDialogActive(true)
                }
              >
                Create Sellar Import credit note
              </DropdownLegacyElement>
            )}
          </SupplierOnly>
          <AdminOnly>
            {!order.customInvoiceNumber &&
              order.paymentType !== "cardPaymentWithDeposit" && (
                <DropdownLegacyElement
                  onClick={() => actions.setXeroInvoiceDialogActive(true)}
                >
                  Create Xero Invoice
                </DropdownLegacyElement>
              )}
          </AdminOnly>
          <AdminOnly>
            {order.fulfillmentType === "sellarDistribution" && (
              <SupplierOnly>
                <DropdownLegacyElement
                  onClick={() => {
                    actions.setAdminOrderAssignRouteAndCreateCtTaskActive(true);
                  }}
                >
                  Assign Route & Create CT task
                </DropdownLegacyElement>
              </SupplierOnly>
            )}
          </AdminOnly>
          <AdminOnly>
            {order.paymentType === "cardPaymentWithDeposit" &&
              hasCardDepositBeenPaid &&
              !order.paidAt && (
                <DropdownLegacyElement
                  onClick={() =>
                    actions.setAdminChargeRemainingOrderBalanceActive(true)
                  }
                >
                  Charge buyer remaining order balance
                </DropdownLegacyElement>
              )}
            {order.fulfillmentType === "sellarDistribution" &&
              supplierUsesPalletInboundForOrder &&
              !orderHasPalletOrderShipment && (
                <DropdownLegacyElement
                  onClick={() =>
                    actions.setAdminAddOrderToPalletDialogActive(true)
                  }
                >
                  Add order to pallet
                </DropdownLegacyElement>
              )}
            {orderHasPalletOrderShipment && (
              <DropdownLegacyElement
                onClick={() =>
                  actions.setAdminRemoveOrderFromPalletDialogActive(true)
                }
              >
                Remove order from pallet
              </DropdownLegacyElement>
            )}
          </AdminOnly>
          {showOrderInvoice(order) && !canUseSellarCreditFeatures && (
            <DropdownLegacyElement
              onClick={() => actions.setInvoiceCreateDialogActive(true)}
            >
              Access VAT invoice
            </DropdownLegacyElement>
          )}
          {showOrderInvoice(order) &&
            canUseSellarCreditFeatures &&
            Boolean(order.dispatchedAt) && (
              <DropdownLegacyElement
                onClick={() =>
                  actions.setSellarCreditInvoiceCreateDialogActive(true)
                }
              >
                Access VAT invoice
              </DropdownLegacyElement>
            )}
        </>
      )}

      <BuyerOnly>
        {showOrderInvoice(order) && (
          <DropdownLegacyElement
            onClick={() => {
              const url = [
                "https://sellar.typeform.com/to/f3vQKqcy#user_email=",
                userEmail,
                "&retailer_name=",
                order.Retailer.Business.name,
                "&supplier_name=",
                order.Supplier.Business.name,
                "&order_id=",
                order.id,
              ].join("");
              window.open(url, "_blank");
            }}
          >
            Report issue with invoice
          </DropdownLegacyElement>
        )}
        {showSendCardPaymentLink && (
          <DropdownLegacyElement onClick={onSendCardPaymentLink}>
            View payment link
          </DropdownLegacyElement>
        )}
        {showCardPaymentLink && (
          <DropdownLegacyElement
            onClick={() => {
              const code = order.PaymentLink?.code;
              if (!code) return;
              navigate(PAYMENT_LINK_REF.href({ code }));
            }}
          >
            View payment link
          </DropdownLegacyElement>
        )}
      </BuyerOnly>

      {showIssueRefund(order) && isActive("ORDER_PARTIAL_REFUNDS") && (
        <DropdownLegacyElement
          onClick={() => actions.setIssueRefundDialogActive(true)}
        >
          Issue partial refund
        </DropdownLegacyElement>
      )}
      {business.Supplier && !order.deliveredAt && !order.cancelledAt && (
        <DropdownLegacyElement
          onClick={() => actions.setModifyOrderModalActive(true)}
        >
          Edit order details
        </DropdownLegacyElement>
      )}
      {showMarkOrderWithIncompleteStripePaymentAsPaid && (
        <SupplierOnly>
          <DropdownLegacyElement
            onClick={() =>
              actions.setOrderWithStripePaymentPaidDialogActive(true)
            }
          >
            Mark as paid
          </DropdownLegacyElement>
        </SupplierOnly>
      )}
      {showRecordCardPaymentWithDepositManualPayment && (
        <SupplierOnly>
          <DropdownLegacyElement
            onClick={() =>
              actions.setCardPaymentWithDepositManualPaymentDialogActive(true)
            }
          >
            Record manual payment
          </DropdownLegacyElement>
        </SupplierOnly>
      )}
      {showOrderCancel({ order, business, canUseSellarCreditFeatures }) && (
        <DropdownLegacyElement
          className={"text-red-500"}
          onClick={onCancelFlow}
        >
          {state.cancelOrderText}
        </DropdownLegacyElement>
      )}
    </>
  );
};
